import { useCallback, useEffect, useRef, useState } from 'react';
import {
  exchangeSessionForToken,
  getGatewayHttpUrl,
  resolveGatewayHttpUrl,
} from '../../auth/serviceToken';
import { MpClient } from '../MpClient';
import type { ChatMessage, LobbyView, MatchInfo } from '../types';

const DEFAULT_GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'ws://localhost:4001';

export interface QueueStatusInfo {
  readonly format: string;
  readonly waitMs: number;
}

export interface QueueErrorInfo {
  readonly message: string;
}

/**
 * Connect an MpClient to the gateway iff multiplayer is enabled for the
 * current view. Pure helper extracted from the hook so it's testable
 * without a DOM; the hook is a thin React wrapper around this.
 *
 * Returns nothing. Errors are intentionally swallowed so a missing
 * match-server doesn't break single-player UI.
 */
export function maybeConnectGateway(client: MpClient, gatewayUrl: string, enabled: boolean): void {
  if (!enabled) return;
  client.connectGateway(gatewayUrl).catch(() => {
    // Gateway unreachable — caller handles via phase check.
  });
}

export interface UseMpLobbyReturn {
  phase: 'connecting' | 'lobby' | 'match';
  lobby: LobbyView | null;
  matchInfo: MatchInfo | null;
  chatHistory: readonly ChatMessage[];
  createLobby: (displayName: string) => void;
  joinLobby: (inviteCode: string, displayName: string) => void;
  leaveLobby: () => void;
  startMatch: () => void;
  addAiSlot: (
    displayName: string,
    difficulty: 'easy' | 'normal' | 'hard',
    aiFogOfWar?: boolean,
  ) => void;
  removeAiSlot: (slotId: string) => void;
  updateConfig: (
    maxPlayers: number,
    mapSize: 'small' | 'medium' | 'large',
    format?: string,
    presetId?: string,
    teams?: boolean,
  ) => void;
  setReady: () => void;
  setUnready: () => void;
  sendChat: (sender: string, text: string) => void;
  sendMessage: (payload: Record<string, unknown>) => void;
  getCurrentPlayerId: () => string | undefined;
  /** Ranked/quickplay queue. Exchanges the session for a service token and
   *  reconnects the gateway socket authed if needed, then joins the queue. */
  joinQueue: (format: string, ranked: boolean, displayName: string) => void;
  leaveQueue: () => void;
  queueStatus: QueueStatusInfo | null;
  queueError: QueueErrorInfo | null;
  /** True while a ranked/quickplay join is negotiating the token exchange. */
  queuePending: boolean;
  onMatchFound: (cb: (info: MatchInfo) => void) => () => void;
  rematch: () => void;
}

export function useMpLobby(gatewayUrl = DEFAULT_GATEWAY_URL, enabled = true): UseMpLobbyReturn {
  const clientRef = useRef<MpClient | null>(null);
  const [phase, setPhase] = useState<'connecting' | 'lobby' | 'match'>('connecting');
  const [lobby, setLobby] = useState<LobbyView | null>(null);
  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null);
  const [chatHistory, setChatHistory] = useState<readonly ChatMessage[]>([]);
  const [queueStatus, setQueueStatus] = useState<QueueStatusInfo | null>(null);
  const [queueError, setQueueError] = useState<QueueErrorInfo | null>(null);
  const [queuePending, setQueuePending] = useState(false);
  const matchFoundCbRef = useRef<((info: MatchInfo) => void) | null>(null);

  useEffect(() => {
    const client = new MpClient();
    clientRef.current = client;

    const unsubs = [
      client.on('lobby:updated', () => {
        setLobby(client.getLobbyView());
        setPhase('lobby');
      }),
      client.on('match:found', (info) => {
        const mi = info as unknown as MatchInfo;
        setMatchInfo(mi);
        setQueueStatus(null);
        setQueueError(null);
        setPhase('match');
        matchFoundCbRef.current?.(mi);
      }),
      client.on('queue:status', (msg) => {
        const m = msg as { format?: string; waitMs?: number };
        setQueueStatus({ format: m.format ?? '', waitMs: m.waitMs ?? 0 });
        setQueueError(null);
      }),
      client.on('queue:error', (msg) => {
        setQueueError({ message: (msg as { message?: string }).message ?? 'queue error' });
        setQueueStatus(null);
      }),
      client.on('chat', () => {
        setChatHistory([...client.getChatHistory()]);
      }),
    ];

    maybeConnectGateway(client, gatewayUrl, enabled);

    return () => {
      for (const u of unsubs) {
        u();
      }
      client.disconnect();
    };
  }, [gatewayUrl, enabled]);

  const createLobby = useCallback((displayName: string) => {
    clientRef.current?.createLobby(displayName);
  }, []);

  const joinLobby = useCallback((inviteCode: string, displayName: string) => {
    clientRef.current?.joinLobby(inviteCode, displayName);
  }, []);

  const leaveLobby = useCallback(() => {
    clientRef.current?.leaveLobby();
    setPhase('connecting');
  }, []);

  const startMatch = useCallback(() => {
    clientRef.current?.startMatch();
  }, []);

  const addAiSlot = useCallback(
    (displayName: string, difficulty: 'easy' | 'normal' | 'hard', aiFogOfWar?: boolean) => {
      clientRef.current?.addAiSlot(displayName, difficulty, aiFogOfWar);
    },
    [],
  );

  const removeAiSlot = useCallback((slotId: string) => {
    clientRef.current?.removeAiSlot(slotId);
  }, []);

  const updateConfig = useCallback(
    (
      maxPlayers: number,
      mapSize: 'small' | 'medium' | 'large',
      format?: string,
      presetId?: string,
      teams?: boolean,
    ) => {
      clientRef.current?.updateConfig(maxPlayers, mapSize, format, presetId, teams);
    },
    [],
  );

  const setReady = useCallback(() => {
    clientRef.current?.setReady();
  }, []);

  const setUnready = useCallback(() => {
    clientRef.current?.setUnready();
  }, []);

  const sendChat = useCallback((sender: string, text: string) => {
    clientRef.current?.sendChat(sender, text);
  }, []);

  const sendMessage = useCallback((payload: Record<string, unknown>) => {
    clientRef.current?.sendMessage(payload);
  }, []);

  const getCurrentPlayerId = useCallback(() => {
    return clientRef.current?.getCurrentPlayerId();
  }, []);

  const joinQueue = useCallback(
    (format: string, ranked: boolean, displayName: string) => {
      const client = clientRef.current;
      if (!client) return;
      setQueueError(null);
      setQueuePending(true);
      void (async () => {
        try {
          if (ranked) {
            const gwHttp = getGatewayHttpUrl() ?? resolveGatewayHttpUrl(gatewayUrl);
            if (!gwHttp) {
              setQueueError({ message: 'sign in to play ranked' });
              return;
            }
            const cred = await exchangeSessionForToken(gwHttp);
            if (!cred) {
              setQueueError({ message: 'sign in to play ranked' });
              return;
            }
            client.setAuthIdentity(cred.sub);
            const authed = await client.ensureAuthedGateway(cred.token);
            if (!authed) {
              setQueueError({ message: 'could not authenticate the gateway connection' });
              return;
            }
          }
          client.joinQueue(format, ranked, displayName);
        } finally {
          setQueuePending(false);
        }
      })();
    },
    [gatewayUrl],
  );

  const leaveQueue = useCallback(() => {
    clientRef.current?.leaveQueue();
    setQueueStatus(null);
  }, []);

  const onMatchFound = useCallback((cb: (info: MatchInfo) => void) => {
    matchFoundCbRef.current = cb;
    return () => {
      matchFoundCbRef.current = null;
    };
  }, []);

  const rematch = useCallback(() => {
    setMatchInfo(null);
    setPhase('lobby');
  }, []);

  return {
    phase,
    lobby,
    matchInfo,
    chatHistory,
    createLobby,
    joinLobby,
    leaveLobby,
    startMatch,
    addAiSlot,
    removeAiSlot,
    updateConfig,
    setReady,
    setUnready,
    sendChat,
    onMatchFound,
    rematch,
    sendMessage,
    getCurrentPlayerId,
    joinQueue,
    leaveQueue,
    queueStatus,
    queueError,
    queuePending,
  };
}
